/**
 * A cor do componente no diagrama — escolhida entre as **cores de função**, não
 * num seletor RGB.
 *
 * A cor não é enfeite: é a legenda do diagrama. `#FFFBD6` quer dizer "Injeção
 * Eletrônica", e o cliente lê o significado. A produção segue esse padrão à
 * risca, e o seletor RGB deixava pintar de qualquer coisa — pior, gravava o hex
 * em minúsculas (`#fffbd6`) e o componente saía da legenda mesmo quando a cor
 * era a certa. O sistema antigo já era assim: um `<select>` das cores de função.
 *
 * Medido em 28/09/2026: sete cores no catálogo, e fora dele só 19 componentes
 * em todo o acervo. A cor fora do padrão que já estiver gravada aparece como
 * opção própria, marcada, para ninguém a perder sem querer.
 */

import { useQuery } from "@tanstack/react-query"
import { obter } from "@/api/cliente"

type CorDeFuncao = { cor: string; titulo: string }

export function usarCoresDeFuncao() {
  return useQuery({
    // A mesma chave da tela "Cores de função": editar lá atualiza aqui.
    queryKey: ["cores-funcao"] as const,
    queryFn: () => obter<CorDeFuncao[]>("api/cores-funcao", {}),
    staleTime: 10 * 60_000,
  })
}

const igual = (a: string, b: string) => a.trim().toUpperCase() === b.trim().toUpperCase()

export function SeletorDeCorDeFuncao({ valor, aoMudar, rotulo = "Cor do componente", semCor = true }: {
  valor: string
  aoMudar: (cor: string) => void
  rotulo?: string
  /** O principal do diagrama sempre tem cor — no legado não havia "sem cor" para ele. */
  semCor?: boolean
}) {
  const cores = usarCoresDeFuncao()
  const lista = cores.data ?? []
  const foraDoPadrao = valor.trim() !== "" && cores.data != null && !lista.some((c) => igual(c.cor, valor))

  const opcoes: { cor: string; titulo: string; aviso?: boolean }[] = [
    ...(semCor ? [{ cor: "", titulo: "Sem cor" }] : []),
    ...lista,
    ...(foraDoPadrao ? [{ cor: valor, titulo: `Fora do padrão (${valor})`, aviso: true }] : []),
  ]

  if (cores.isPending) return <span style={{ fontSize: 11, color: "var(--txt3)" }}>carregando as cores…</span>
  if (cores.error) return <span style={{ fontSize: 11, color: "var(--erro)" }}>As cores de função não vieram.</span>

  return (
    <div role="radiogroup" aria-label={rotulo} style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
      {opcoes.map((o) => {
        const escolhida = o.cor === "" ? valor.trim() === "" : igual(o.cor, valor)
        return (
          <button
            key={o.cor || "sem-cor"}
            type="button"
            role="radio"
            aria-checked={escolhida}
            title={o.cor ? `${o.titulo} — ${o.cor.toUpperCase()}` : o.titulo}
            // Grava o valor do catálogo, com a caixa dele — é a chave da legenda.
            onClick={() => aoMudar(o.cor)}
            style={{
              display: "inline-flex", alignItems: "center", gap: 6, height: 26, padding: "0 8px 0 5px",
              borderRadius: 6, fontSize: 11, cursor: "pointer", background: "var(--surface)",
              color: o.aviso ? "var(--erro)" : "var(--txt)",
              border: `1.5px solid ${escolhida ? "var(--brand)" : "var(--line2)"}`,
              fontWeight: escolhida ? 600 : 400,
            }}
          >
            <span aria-hidden="true" style={{
              width: 16, height: 16, borderRadius: 4, flex: "0 0 16px",
              border: "1px solid rgba(0,0,0,.25)",
              background: o.cor || "repeating-linear-gradient(45deg, #fff 0 3px, #ddd 3px 6px)",
            }} />
            {o.titulo}
          </button>
        )
      })}
    </div>
  )
}
